// Driver cash: what Uber handed the man in notes, and what he did with it.
//
// THE SHAPE OF THIS SCREEN IS AN ARGUMENT, so it is worth stating before the code.
//
// The cash identity has four inputs and they fail independently. Uber's cash
// figure, the driver's declaration, the bank statement and the fuel report. On the
// day this was built, three of the four were absent for most shifts and the fourth
// had never been read by anything. A dashboard that answers "how much is missing"
// on those inputs will produce a number that is mostly our own broken plumbing, and
// somebody will take that number to a driver.
//
// So the page leads with the coverage banner, separates exact variance from
// indicative variance everywhere, and colours a gap in our records differently from
// a question for a person. cashModel.ts holds that decision once so no component can
// quietly undo it.
//
// July 2026 is worth knowing as you read it: on the only month where both form
// parts landed, the exact variance across 169 verdicts was MINUS R9,652, more cash
// accounted for than collected. Whatever this screen shows on a month with a broken
// form, that is the honest baseline it should be read against.
//
// 18 SEPTEMBER 2026, AND IT CHANGES HOW THIS PAGE MUST BE READ.
//
// The first Fleetly bank statement was loaded, nineteen days after the plumbing to
// hold one was built. Until that day bank_statement_lines held zero rows, so the
// fourth input was not merely weak, it was absent, and every deposit on this page
// read NO_STATEMENT_LOADED. The August 2026 statement says drivers banked R3,000 in
// cash across thirteen deposits. The same month's declarations say R116,158 across
// 243 of them.
//
// THAT GAP IS NOT A FINDING AND THIS PAGE MUST NEVER RENDER IT AS ONE. Only one
// Fleetly account has ever been registered and no statement had ever been loaded, so
// nobody has ever been in a position to check. A driver told to bank somewhere
// Fleetly never recorded looks exactly like this.
//
// So the Position tab carries two figures and never one: owed on what he DECLARED,
// and owed on what we can PROVE, with the difference between them labelled as
// evidence Fleetly does not hold. The proven figure is NULL, never zero, in a month
// whose statement was not loaded. v_driver_cash_position holds that decision once,
// in Postgres, so no component here can quietly undo it.
//
// 19 SEPTEMBER 2026: A THIRD VARIANCE, BECAUSE TWO OF THEM CANNOT COVER THE YEARS.
//
// Exact variance and indicative variance both read off shift_form_submissions,
// whose first row is 23 July 2026. So seventeen of the twenty months Fleetly has
// traded had two empty cells where the unexplained-cash figures belong, and an
// empty cell reads as a broken page rather than as an absent answer.
//
// THE FIRST VERSION OF THIS COMMENT SAID THE DRIVERS WERE NEVER ASKED. THEY WERE.
//
// The daily Typeform qind02rW has carried "Please type in your cash deposits for
// the shift" since 3 February 2025. Every response answers it. Typeform holds
// 12,466 of them up to 23 July 2026 and 10,070 Part B responses over the same
// period, checked against the Typeform API on 19 September 2026. None has ever
// been imported: the webhook that writes shift_form_submissions was wired on
// 23 July 2026, and the legacy Google Sheets import took the fuel rows and the
// carwash rows and left the deposit answers behind.
//
// So the cells say "not imported", which is true, is about Fleetly rather than
// about a driver, and names the work. Filling them by any other means would be
// inventing a finding, and a finding lands on a man.
//
// 20 SEPTEMBER 2026: THE ANSWERS ARE IN, AND THE PAGE CHANGES MEANING AGAIN.
//
// 22,536 Typeform responses, 12,466 Part A and 10,070 Part B, were pulled and
// placed on the shifts the platform already held. 21,116 found a shift. ZERO
// shifts were created: the platform's shift record is the authority and the
// backfill never argued with it.
//
// Three things came out of doing it that are worth more than the figures:
//
//   The shift used to start at three. map_shift_window_v1 knew only the four
//   o'clock wording, so February to May 2025 matched 2 responses out of 576.
//
//   Nothing ever carried a declaration into the cash identity by itself.
//   ingest_shift_cash_v1 had been run by hand exactly once, on 30 August, so
//   September's 367 declarations were sitting unread and this page said the
//   month banked R0.00. There is a trigger now.
//
//   641 shifts carry more than one Part A response and the FIRST one was
//   winning, so 92 cash rows stated figures no submission on file supported.
//
// WHAT THE FIGURES SAY, AND IT IS NOT WHAT THE AUGUST GAP SUGGESTED. Across the
// sixteen months that now carry an exact variance, the total is MINUS R46,868:
// more cash accounted for than Uber says was collected. That is what a fleet
// where cash is broadly being accounted for looks like, and it is the opposite
// of the picture a declared-versus-proven gap on its own would paint.
//
// The gap is still there and still unexplained: R957,822 declared banked against
// R291,420 a bank statement can demonstrate. Both numbers belong on the screen
// and neither one is a finding about a driver until somebody establishes whether
// account 63115351396 is the only one that ever received driver cash.
//
// So there is a third bound, variance_evidence_zar, which needs no declaration:
// cash received less what a bank statement or a photographed till slip can
// demonstrate. It exists in every month on the same rule, so it is the only
// column here that can be read downward. It is also by far the loosest, roughly
// R260,000 in August 2026 against an exact variance of R1,270 on the same month.
// Those two are about different things and this page must never present them as
// the ends of a range.
import { Fragment, useCallback, useEffect, useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import {
  Banknote,
  ShieldAlert,
  AlertCircle,
  Fuel,
  Landmark,
  Upload,
  Users,
  ListChecks,
  MapPin,
  FileText,
  Wand2,
  ChevronRight,
} from 'lucide-react'
import {
  useGetCashCoverage,
  useGetCashSummary,
  useGetCashByDriver,
  useGetCashShifts,
  useGetDepositBankStatus,
  useGetFuelReconciliation,
  useGetFuelTransactions,
  useGetFuelStations,
  useIngestFuelReport,
  useGetDriverCashPosition,
  useGetCashDocuments,
  useGetCashDocumentUrl,
  useUploadCashDocument,
  useConfirmCashDocument,
  useRejectCashDocument,
  useRecordCashResolution,
  useGetCashResolutions,
  useGetDriverCreditCandidates,
  useAttributeBankCredit,
  useUnattributeBankCredit,
  useReadShiftDocuments,
  useApplyReceiptCash,
  useGetOverDeclaredReceipts,
} from '../hooks/backend/cash'
import {
  useGetBankAccounts,
  useIngestBankStatement,
  useMatchDeposits,
  useGetUnmatchedBankCredits,
  useProposeDepositMatches,
  useGetStatementAccess,
  useUploadBankStatementPdf,
} from '../hooks/backend/banking'
import { useCapabilities } from '../hooks/useCapabilities'
import { Spinner } from '../lib/shadcn/spinner'
import { Button } from '../lib/shadcn/button'
import { NativeSelect } from '../lib/shadcn/native-select'
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../lib/shadcn/tabs'
import { Chip } from '../components/interviews/Chip'
import { CoverageBanner, type Coverage } from '../components/cash/CoverageBanner'
import { ImportDialog, type FieldSpec } from '../components/cash/ImportDialog'
import { CashDocumentUpload, type IntakeRow, type IntakeKind, type IntakeFile } from '../components/cash/CashDocumentUpload'
import { BulkBar, DocumentRow, isReady } from '../components/cash/CashDocumentBoard'
import { DriverCashPanel } from '../components/cash/DriverCashPanel'
import { DriverCreditPanel } from '../components/cash/DriverCreditPanel'
import { DriverOutstandingPanel } from '../components/cash/DriverOutstandingPanel'
import { ReceiptReadPanel } from '../components/cash/ReceiptReadPanel'
import { OverDeclaredPanel } from '../components/cash/OverDeclaredPanel'
import { toast } from '../lib/shadcn/sonner'
import { Checkbox } from '../lib/shadcn/checkbox'
import {
  cashStatusLabel,
  cashStatusTone,
  bankStatusLabel,
  bankStatusTone,
  fuelStatusLabel,
  fuelStatusTone,
  presenceLabel,
  presenceTone,
  zar,
  num,
  monthOptions,
  monthOptionsFrom,
  noDeclarationOnFile,
} from '../components/cash/cashModel'

type Row = Record<string, any>

const BANK_FIELDS: FieldSpec[] = [
  {
    key: 'txn_date',
    label: 'Transaction date',
    kind: 'date',
    required: true,
    candidates: ['date', 'transaction date', 'txn date', 'posting date', 'value date'],
    help: 'Read day first, which is the South African convention.',
  },
  {
    key: 'amount_zar',
    label: 'Amount',
    kind: 'amount',
    required: true,
    candidates: ['amount', 'transaction amount', 'value'],
    help: 'A credit into the account must be positive. A deposit is a credit.',
  },
  { key: 'description', label: 'Description', kind: 'text', candidates: ['description', 'narrative', 'details'] },
  { key: 'reference', label: 'Reference', kind: 'text', candidates: ['reference', 'ref', 'their reference'] },
  { key: 'balance_zar', label: 'Balance', kind: 'amount', candidates: ['balance', 'running balance'] },
]

const FUEL_FIELDS: FieldSpec[] = [
  {
    key: 'transaction_at',
    label: 'Transaction date and time',
    kind: 'datetime',
    required: true,
    candidates: ['date', 'transaction date', 'date time', 'timestamp'],
    help: 'The time matters: the Cartrack check asks where the car was at that moment.',
  },
  { key: 'amount_zar', label: 'Amount', kind: 'amount', required: true, candidates: ['amount', 'value', 'total'] },
  {
    key: 'registration',
    label: 'Registration',
    kind: 'text',
    candidates: ['registration', 'reg', 'vehicle', 'fleet number', 'plate'],
    help: 'Either this or the card number is needed to attribute the spend to a car.',
  },
  { key: 'fleet_card_number', label: 'Card number', kind: 'text', candidates: ['card', 'card number', 'fleet card'] },
  { key: 'litres', label: 'Litres', kind: 'number', candidates: ['litres', 'liters', 'quantity', 'volume'] },
  { key: 'station', label: 'Station', kind: 'text', candidates: ['station', 'merchant', 'site', 'location'] },
  { key: 'product', label: 'Product', kind: 'text', candidates: ['product', 'fuel type', 'grade'] },
]

function Stat({
  label,
  value,
  hint,
  tone,
}: {
  label: string
  value: string
  hint?: string
  tone?: 'plain' | 'good' | 'warn'
}) {
  return (
    <div className="rounded-md border border-border bg-background px-3 py-2">
      <div className="text-[11px] uppercase tracking-wide text-muted-foreground">{label}</div>
      <div
        className={
          tone === 'good'
            ? 'text-lg font-semibold tabular-nums text-emerald-700 dark:text-emerald-400'
            : tone === 'warn'
              ? 'text-lg font-semibold tabular-nums text-amber-700 dark:text-amber-400'
              : 'text-lg font-semibold tabular-nums text-foreground'
        }
      >
        {value}
      </div>
      {hint && <div className="mt-0.5 text-[11px] text-muted-foreground">{hint}</div>}
    </div>
  )
}

export default function DriverCash() {
  const { loading: accessLoading, canView, canEditFleet, canEditDrivers, canViewFinance, caption } =
    useCapabilities()
  const [month, setMonth] = useState(monthOptions(1)[0]?.value ?? '')
  const [tab, setTab] = useState('overview')
  const [onlyAttention, setOnlyAttention] = useState(true)
  const [importing, setImporting] = useState<'bank' | 'fuel' | null>(null)
  const [uploadingDoc, setUploadingDoc] = useState(false)
  const [intakeRows, setIntakeRows] = useState<IntakeRow[]>([])
  const [intakeBusy, setIntakeBusy] = useState(false)
  const [picked, setPicked] = useState<Set<string>>(new Set())
  const [bulkBusy, setBulkBusy] = useState(false)
  const [bulkProgress, setBulkProgress] = useState<string | null>(null)
  const [openDriver, setOpenDriver] = useState<string | null>(null)
  // Separate from openDriver on purpose: the By driver tab and the Position tab
  // each open a man's row, and sharing one piece of state would open him on both.
  const [openOnList, setOpenOnList] = useState<string | null>(null)
  const [uploadFor, setUploadFor] = useState<string | null>(null)
  const [readBusy, setReadBusy] = useState(false)
  const [readProgress, setReadProgress] = useState<string | null>(null)
  const [lastRead, setLastRead] = useState<Row | null>(null)
  const [lastApply, setLastApply] = useState<Row | null>(null)

  const coverage = useGetCashCoverage()
  const summary = useGetCashSummary()
  const byDriver = useGetCashByDriver()
  const shifts = useGetCashShifts()
  const deposits = useGetDepositBankStatus()
  const fuel = useGetFuelReconciliation()
  const fuelTx = useGetFuelTransactions()
  const stations = useGetFuelStations()
  const accounts = useGetBankAccounts()
  const credits = useGetUnmatchedBankCredits()
  const ingestBank = useIngestBankStatement()
  const ingestFuel = useIngestFuelReport()
  const matchDeposits = useMatchDeposits()
  const proposals = useProposeDepositMatches()
  const position = useGetDriverCashPosition()
  const documents = useGetCashDocuments()
  const docUrl = useGetCashDocumentUrl()
  const uploadDoc = useUploadCashDocument()
  const confirmDoc = useConfirmCashDocument()
  const rejectDoc = useRejectCashDocument()
  const resolve = useRecordCashResolution()
  const resolutions = useGetCashResolutions()
  const statementAccess = useGetStatementAccess()
  const uploadStatement = useUploadBankStatementPdf()
  const candidates = useGetDriverCreditCandidates()
  const attribute = useAttributeBankCredit()
  const unattribute = useUnattributeBankCredit()
  const readDocs = useReadShiftDocuments()
  const applyReceipts = useApplyReceiptCash()
  const overDeclared = useGetOverDeclaredReceipts()

  // The month list runs from Fleetly's first shift to this month, not back a
  // fixed number of months from today. Until 19 September 2026 it was a rolling
  // 14, which reached August 2025 while the first shift was 3 February 2025:
  // seven months of the company's own trading were simply not selectable from
  // this page, and the far end got one month shorter every time a month turned.
  const coverageRow = coverage.data as Row | null | undefined
  const months = useMemo(
    () => monthOptionsFrom(coverageRow?.shifts_from as string | undefined),
    [coverageRow?.shifts_from],
  )

  const loadAll = useCallback(() => {
    coverage.trigger({}, { skipCache: true })
    summary.trigger({}, { skipCache: true })
    byDriver.trigger({ month }, { skipCache: true })
    shifts.trigger({ month, status: onlyAttention ? 'needs_attention' : undefined }, { skipCache: true })
    deposits.trigger({ month }, { skipCache: true })
    fuel.trigger({ month, status: onlyAttention ? 'needs_attention' : undefined }, { skipCache: true })
    fuelTx.trigger({ presence: onlyAttention ? 'needs_attention' : undefined }, { skipCache: true })
    stations.trigger({}, { skipCache: true })
    position.trigger({ month }, { skipCache: true })
    documents.trigger({ limit: 200 }, { skipCache: true })
    resolutions.trigger({ period_month: month }, { skipCache: true })
    statementAccess.trigger({}, { skipCache: true })
    overDeclared.trigger({ limit: 500 }, { skipCache: true })
    if (canViewFinance) {
      accounts.trigger({}, { skipCache: true })
      credits.trigger({}, { skipCache: true })
    }
    // The hook objects are recreated each render; depending on them would loop.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [month, onlyAttention, canViewFinance])

  useEffect(() => {
    if (canView) loadAll()
  }, [canView, loadAll])

  // The driver list every picker on this page uses. One source, so a name that
  // appears in the bulk bar is the same name that appears on the row.
  const driverOptions = useMemo(
    () =>
      ((byDriver.data as Row[] | undefined) ?? []).map((x) => ({
        driver_id: String(x.driver_id),
        driver_name: String(x.driver_name),
      })),
    [byDriver.data],
  )

  // The proven position for this same month, keyed by driver, so the By driver
  // table can put what is VALIDATED beside what was DECLARED without a second
  // round trip. position.trigger runs on the same month in loadAll.
  //
  // Two columns and not one, and neither of them replaces the declared column.
  // A single "validated" figure on that table would read as a finding about a
  // man in every month whose statement was never loaded, which is most of them.
  const provenByDriver = useMemo(() => {
    const out = new Map<string, Row>()
    for (const r of (position.data as Row[] | undefined) ?? []) {
      out.set(String(r.driver_id), r)
    }
    return out
  }, [position.data])

  const openCredits = useCallback(
    (driverId: string) => {
      const next = openOnList === driverId ? null : driverId
      setOpenOnList(next)
      if (next) candidates.trigger({ driver_id: next }, { skipCache: true })
      // eslint-disable-next-line react-hooks/exhaustive-deps
    },
    [openOnList],
  )

  const allDocs = useMemo(() => (documents.data as Row[] | undefined) ?? [], [documents.data])
  // Waiting on a person: read or not read, never one already decided.
  const openDocs = useMemo(
    () => allDocs.filter((d) => d.status === 'read' || d.status === 'uploaded'),
    [allDocs],
  )
  const pickedDocs = useMemo(
    () => allDocs.filter((d) => picked.has(String(d.document_id))),
    [allDocs, picked],
  )

  // Bulk acts run ONE AT A TIME against the same guarded functions a single row
  // calls. No bulk endpoint exists and none should: the refusals in
  // cash_evidence_confirm_v1 are what stop an unattributed amount being asserted
  // about a man, and a batch path that skipped them would be a second door with
  // weaker locks on it.
  const runBulk = useCallback(
    async (act: 'assign' | 'confirm' | 'reject', arg?: string) => {
      const rows = act === 'confirm' ? pickedDocs.filter(isReady) : pickedDocs
      if (rows.length === 0) return
      setBulkBusy(true)
      let done = 0
      let failed = 0
      let firstError = ''
      for (const d of rows) {
        setBulkProgress(`${done + failed + 1} of ${rows.length}: ${String(d.file_name ?? '')}`)
        try {
          const res =
            act === 'reject'
              ? ((await rejectDoc.trigger(
                  { document_id: String(d.document_id), reason: arg ?? '' },
                  { skipCache: true },
                ).result) as Row)
              : ((await confirmDoc.trigger(
                  {
                    document_id: String(d.document_id),
                    // Assigning is a confirmation carrying the driver the manager
                    // named and the date and amount already read off the document.
                    driver_id: act === 'assign' ? (arg ?? '') : String(d.driver_id ?? ''),
                    doc_date: String(d.doc_date ?? ''),
                    amount_zar: Number(d.amount_zar ?? 0),
                  },
                  { skipCache: true },
                ).result) as Row)
          if (res?.ok) done += 1
          else {
            failed += 1
            if (!firstError) firstError = String(res?.error ?? '')
          }
        } catch (err) {
          failed += 1
          if (!firstError) firstError = err instanceof Error ? err.message : String(err)
        }
      }
      setBulkProgress(null)
      setBulkBusy(false)
      setPicked(new Set())
      if (done > 0) {
        toast.success(
          `${done} document${done === 1 ? '' : 's'} ${act === 'reject' ? 'rejected' : 'confirmed'}.` +
            (failed > 0 ? ` ${failed} were refused.` : ''),
        )
      }
      if (failed > 0 && done === 0) {
        toast.error('Nothing changed', { description: firstError })
      }
      loadAll()
    },
    // The hook objects are recreated each render; depending on them would loop.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [pickedDocs, loadAll],
  )

  const cover = (coverage.data as Coverage | null) ?? null
  const summaryRows = (summary.data as Row[] | undefined) ?? []
  const thisMonth = summaryRows.find((r) => String(r.shift_month).slice(0, 10) === month)

  if (accessLoading) {
    return (
      <div className="flex h-full items-center justify-center gap-2 text-sm text-muted-foreground">
        <Spinner className="h-4 w-4" /> Checking access…
      </div>
    )
  }

  if (!canView) {
    return (
      <div className="flex h-full items-center justify-center p-6">
        <div className="flex max-w-md flex-col items-center gap-3 rounded-lg border border-border bg-card p-8 text-center">
          <div className="flex h-12 w-12 items-center justify-center rounded-full bg-[hsl(var(--brand-soft))] text-[hsl(var(--brand-soft-foreground))]">
            <ShieldAlert className="h-6 w-6" />
          </div>
          <p className="text-sm font-medium text-foreground">
            Driver cash is available to cockpit users. Ask an admin if you need it.
          </p>
        </div>
      </div>
    )
  }

  return (
    <div className="flex h-full min-h-0 flex-col bg-card">
      <div className="border-b border-border px-4 py-3">
        <div className="flex items-center gap-3">
          <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-[hsl(var(--brand-soft))] text-[hsl(var(--brand-soft-foreground))]">
            <Banknote className="h-5 w-5" />
          </div>
          <h1 className="text-lg font-semibold text-foreground">Driver cash</h1>
          <NativeSelect
            className="ml-auto w-48"
            value={month}
            onChange={(e) => setMonth(e.target.value)}
            aria-label="Month"
          >
            {months.map((m) => (
              <option key={m.value} value={m.value}>
                {m.label}
              </option>
            ))}
          </NativeSelect>
        </div>
        <p className="mt-1 text-xs text-muted-foreground">{caption}</p>
      </div>

      <CoverageBanner coverage={cover} />

      <div className="min-h-0 flex-1 overflow-y-auto">
        <Tabs value={tab} onValueChange={setTab} className="w-full">
          <div className="sticky top-0 z-10 border-b border-border bg-card px-3 py-2">
            <TabsList>
              <TabsTrigger value="overview">Overview</TabsTrigger>
              <TabsTrigger value="drivers">By driver</TabsTrigger>
              <TabsTrigger value="position">Position</TabsTrigger>
              <TabsTrigger value="shifts">Shifts</TabsTrigger>
              <TabsTrigger value="deposits">Deposits</TabsTrigger>
              <TabsTrigger value="fuel">Petrol</TabsTrigger>
              <TabsTrigger value="documents">Documents</TabsTrigger>
              <TabsTrigger value="sources">Sources</TabsTrigger>
            </TabsList>
            <label className="mt-2 flex items-center gap-2 text-xs text-muted-foreground">
              <input
                type="checkbox"
                checked={onlyAttention}
                onChange={(e) => setOnlyAttention(e.target.checked)}
              />
              Show only rows that need a person
            </label>
          </div>

          <TabsContent value="overview" className="p-3">
            <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-4">
              <Stat label="Uber cash collected" value={zar(thisMonth?.uber_cash_collected_zar)} />
              <Stat label="Private trip cash" value={zar(thisMonth?.private_trip_cash_zar)} />
              <Stat
                label="Declared as banked"
                value={zar(thisMonth?.deposited_zar)}
                hint={
                  thisMonth?.deposited_zar === null || thisMonth?.deposited_zar === undefined
                    ? 'The daily form asked for this and the drivers answered. Those answers sit in Typeform and have never been imported, so the cockpit holds nothing to show. It is not a claim that nothing was banked.'
                    : `What ${num(thisMonth?.declaring_shifts ?? 0)} shifts said they banked. Never added to the proven figure beside it.`
                }
              />
              <Stat
                label="Proven banked"
                value={zar(thisMonth?.proven_banked_zar)}
                hint={
                  thisMonth?.statement_lines === null || thisMonth?.statement_lines === undefined
                    ? 'No bank statement is loaded for this month, so nothing in it can be proven either way.'
                    : `From the bank statement: ${num(thisMonth?.attributed_credits ?? 0)} credits carrying a driver's name, plus anything a declaration matched.`
                }
              />
              <Stat
                label="Declared on petrol"
                value={zar(thisMonth?.petrol_declared_zar)}
                hint="What the drivers typed into the daily form as petrol bought, whatever they paid with. This has been asked since February 2025."
              />
              <Stat
                label="Proven paid in cash"
                value={zar(thisMonth?.proven_petrol_zar)}
                hint={`Of the figure on its left, the part a photographed till slip shows was paid in cash. ${num(thisMonth?.receipts_read ?? 0)} of ${num(thisMonth?.receipt_rows ?? 0)} receipts read so far.`}
              />
              <Stat
                label="Tender not established"
                value={zar(thisMonth?.petrol_tender_unknown_zar)}
                hint="Declared petrol with no slip read yet, or a slip that does not print how it was paid for. It is not money anybody owes and it is our gap, not a driver's."
              />
              <Stat
                label="Unexplained, exact"
                value={
                  noDeclarationOnFile(thisMonth)
                    ? 'not imported'
                    : zar(thisMonth?.variance_exact_zar, { signed: true })
                }
                hint={
                  noDeclarationOnFile(thisMonth)
                    ? 'The drivers answered both form parts for this month. Those answers are in Typeform and have never been imported into the cockpit, so no variance can be computed here yet. It is our import that is missing, not their declaration.'
                    : `Across ${num(thisMonth?.balanced ?? 0)} balanced, ${num(thisMonth?.shortfall ?? 0)} short and ${num(thisMonth?.surplus ?? 0)} over. This is a finding.`
                }
                tone={
                  noDeclarationOnFile(thisMonth) ? undefined : Number(thisMonth?.variance_exact_zar ?? 0) > 0 ? 'warn' : 'good'
                }
              />
              <Stat
                label="Unexplained, indicative"
                value={
                  noDeclarationOnFile(thisMonth)
                    ? 'not imported'
                    : zar(thisMonth?.variance_indicative_zar, { signed: true })
                }
                hint={
                  noDeclarationOnFile(thisMonth)
                    ? 'Same reason as the tile on its left. Read the evidence figure beside it, which needs no declaration at all.'
                    : 'Part of the declaration is missing on these shifts. Not a finding. Never add this to the figure on its left.'
                }
              />
              <Stat
                label="Not demonstrated"
                value={zar(thisMonth?.variance_evidence_zar, { signed: true })}
                hint={`Cash received, less whatever a bank statement or a till slip can demonstrate. A document demonstrates ${zar(thisMonth?.evidence_proven_zar)} of it this month. This column counts nothing anybody said, so it exists in every month Fleetly has traded. It is the size of our record gap and it is not owed by anybody.${
                  thisMonth?.evidence_has_statement === false
                    ? ' No bank statement is loaded for this month, so nothing banked can be demonstrated in it at all.'
                    : ''
                }`}
              />
              <Stat
                label="Shifts we cannot judge"
                value={num(thisMonth?.missing_data ?? 0)}
                hint="Missing Uber cash or missing submission."
              />
              <Stat label="Deposit slips handed in" value={num(thisMonth?.deposit_slips ?? 0)} />
            </div>

            <h3 className="mt-5 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
              Month by month
            </h3>
            <div className="mt-2 overflow-x-auto rounded-md border border-border">
              <table className="w-full text-xs">
                <thead className="bg-muted/50">
                  <tr>
                    <th className="px-3 py-2 text-left font-medium">Month</th>
                    <th className="px-3 py-2 text-right font-medium">Shifts</th>
                    <th className="px-3 py-2 text-right font-medium">Uber cash</th>
                    <th className="px-3 py-2 text-right font-medium">Banked, declared</th>
                    <th className="px-3 py-2 text-right font-medium">Banked, proven</th>
                    <th className="px-3 py-2 text-right font-medium">Petrol, declared</th>
                    <th className="px-3 py-2 text-right font-medium">Petrol, cash proven</th>
                    <th className="px-3 py-2 text-right font-medium">Tender unknown</th>
                    <th className="px-3 py-2 text-right font-medium">Exact variance</th>
                    <th className="px-3 py-2 text-right font-medium">Indicative</th>
                    <th className="px-3 py-2 text-right font-medium">Not demonstrated</th>
                    <th className="px-3 py-2 text-right font-medium">Cannot judge</th>
                  </tr>
                </thead>
                <tbody>
                  {summaryRows.map((r) => (
                    <tr key={String(r.shift_month)} className="border-t border-border">
                      <td className="whitespace-nowrap px-3 py-1.5">
                        {new Date(String(r.shift_month)).toLocaleDateString('en-ZA', {
                          month: 'short',
                          year: 'numeric',
                        })}
                      </td>
                      <td className="px-3 py-1.5 text-right tabular-nums">{num(r.shifts)}</td>
                      <td className="px-3 py-1.5 text-right tabular-nums">{zar(r.uber_cash_collected_zar)}</td>
                      <td className="px-3 py-1.5 text-right tabular-nums">{zar(r.deposited_zar)}</td>
                      <td
                        className="px-3 py-1.5 text-right tabular-nums"
                        title={
                          r.statement_lines === null || r.statement_lines === undefined
                            ? 'No bank statement is loaded for this month.'
                            : `${num(r.attributed_credits ?? 0)} credits carrying a driver's name.`
                        }
                      >
                        {zar(r.proven_banked_zar)}
                      </td>
                      <td className="px-3 py-1.5 text-right tabular-nums">{zar(r.petrol_declared_zar)}</td>
                      <td
                        className="px-3 py-1.5 text-right tabular-nums"
                        title={`${num(r.receipts_read ?? 0)} of ${num(r.receipt_rows ?? 0)} receipts read.`}
                      >
                        {zar(r.proven_petrol_zar)}
                      </td>
                      <td className="px-3 py-1.5 text-right tabular-nums text-muted-foreground">
                        {zar(r.petrol_tender_unknown_zar)}
                      </td>
                      <td
                        className={
                          noDeclarationOnFile(r)
                            ? 'px-3 py-1.5 text-right text-muted-foreground'
                            : 'px-3 py-1.5 text-right tabular-nums'
                        }
                        title={
                          noDeclarationOnFile(r)
                            ? 'The drivers answered. The answers are in Typeform and have never been imported into the cockpit, so no variance can be computed here yet.'
                            : undefined
                        }
                      >
                        {noDeclarationOnFile(r) ? 'not imported' : zar(r.variance_exact_zar, { signed: true })}
                      </td>
                      <td
                        className={
                          noDeclarationOnFile(r)
                            ? 'px-3 py-1.5 text-right text-muted-foreground'
                            : 'px-3 py-1.5 text-right tabular-nums text-muted-foreground'
                        }
                        title={
                          noDeclarationOnFile(r)
                            ? 'Same reason. Read the column on its right, which needs no declaration.'
                            : undefined
                        }
                      >
                        {noDeclarationOnFile(r) ? 'not imported' : zar(r.variance_indicative_zar, { signed: true })}
                      </td>
                      <td
                        className="px-3 py-1.5 text-right tabular-nums"
                        title={
                          r.evidence_has_statement === false
                            ? 'No bank statement is loaded for this month, so nothing banked can be demonstrated in it.'
                            : `Demonstrated by document this month: ${zar(r.evidence_proven_zar)}.`
                        }
                      >
                        {zar(r.variance_evidence_zar, { signed: true })}
                      </td>
                      <td className="px-3 py-1.5 text-right tabular-nums text-muted-foreground">
                        {num(Number(r.missing_data ?? 0) + Number(r.no_declaration_era ?? 0))}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <p className="mt-2 text-xs text-muted-foreground">
              There are three variance columns and they are never added to each other. Exact is a
              finding: both form parts landed and the driver answered in full. Indicative is an upper
              bound with part of the declaration missing, so the true figure is almost certainly
              lower. Not demonstrated counts nothing anybody said at all.
            </p>
            <p className="mt-1 text-xs text-muted-foreground">
              Every month carries a declared figure from 20 September 2026, when 22,536 responses
              were pulled from Typeform and placed on the shifts the platform already held. 21,116 of
              them found a shift and none was created to receive one. A month can still read "not
              imported" if no shift in it carries a declaration at all, and none does today.
            </p>
            <p className="mt-1 text-xs text-muted-foreground">
              Not demonstrated is the column that runs the whole twenty months on one rule, so it is
              the only one to read down. It is the cash received less what a bank statement or a
              photographed till slip can demonstrate, and it counts no carwash, no petrol without a
              slip, no peer transfer and no deposit declaration. It is the size of the hole in
              Fleetly's own records, it is not a shortfall, and it is not owed by anybody. It runs far
              larger than the exact column on the same month for that reason, and the two are not the
              ends of a range.
            </p>
            <p className="mt-1 text-xs text-muted-foreground">
              Declared and proven are also never added. Declared is what a driver wrote on the form.
              Proven is what a bank statement or a photographed till slip shows.
            </p>
            <p className="mt-1 text-xs text-muted-foreground">
              The petrol columns are three readings of one population. Declared is the daily form,
              asked since February 2025. Cash proven is the part a till slip shows was paid in cash.
              Tender unknown is the rest: a receipt nobody has read yet, or one that does not print
              how it was paid for. Card slips are deliberately not counted as proven, because a slip
              does not say whose card it was.
            </p>
            <p className="mt-1 text-xs text-muted-foreground">
              A declared figure is what the driver typed on the form, subject to a ceiling of R3,000
              banked and R2,000 of his own cash on petrol per shift. Anything above a ceiling is held
              for a person to look at rather than loaded, and five figures between R33,758 and
              R110,929 are sitting there. A blank proven column means no bank statement is loaded for
              that month, which is true of September 2026.
            </p>
          </TabsContent>

          <TabsContent value="drivers" className="p-3">
            {/* The spinner only before the first answer. Coming back to the window
                refetches every read, and swapping the table for a spinner then
                unmounted an open driver's panel: a manager who went to Finder to
                pick a slip came back to a closed row and an empty form. */}
            {byDriver.loading && !byDriver.data ? (
              <div className="flex items-center gap-2 p-6 text-sm text-muted-foreground">
                <Spinner className="h-4 w-4" /> Loading…
              </div>
            ) : (
              <div className="overflow-x-auto rounded-md border border-border">
                <table className="w-full text-xs">
                  <thead className="bg-muted/50">
                    <tr>
                      <th className="px-3 py-2 text-left font-medium">Driver</th>
                      <th className="px-3 py-2 text-right font-medium">Shifts</th>
                      <th className="px-3 py-2 text-right font-medium">Judgeable</th>
                      <th className="px-3 py-2 text-right font-medium">Uber cash</th>
                      <th className="px-3 py-2 text-right font-medium">Banked declared</th>
                      <th className="px-3 py-2 text-right font-medium">Banked validated</th>
                      <th className="px-3 py-2 text-right font-medium">Petrol declared</th>
                      <th className="px-3 py-2 text-right font-medium">Petrol validated</th>
                      <th className="px-3 py-2 text-right font-medium">Exact variance</th>
                      <th className="px-3 py-2 text-right font-medium">Slips</th>
                      <th className="px-3 py-2 text-left font-medium">Form gaps</th>
                    </tr>
                  </thead>
                  <tbody>
                    {((byDriver.data as Row[] | undefined) ?? []).map((r) => {
                      const id = String(r.driver_id)
                      const p = provenByDriver.get(id)
                      const isOpen = openOnList === id
                      return (
                      <Fragment key={id}>
                      <tr className="border-t border-border">
                        <td className="whitespace-nowrap px-3 py-1.5 font-medium">
                          <button
                            type="button"
                            className="flex items-center gap-1 text-left hover:underline"
                            onClick={() => openCredits(id)}
                            aria-expanded={isOpen}
                          >
                            <ChevronRight
                              className={`h-3 w-3 shrink-0 transition-transform ${isOpen ? 'rotate-90' : ''}`}
                            />
                            {r.driver_name}
                          </button>
                        </td>
                        <td className="px-3 py-1.5 text-right tabular-nums">{num(r.shifts)}</td>
                        <td className="px-3 py-1.5 text-right tabular-nums">{num(r.exact_shifts)}</td>
                        <td className="px-3 py-1.5 text-right tabular-nums">{zar(r.uber_cash_collected_zar)}</td>
                        <td className="px-3 py-1.5 text-right tabular-nums">{zar(r.deposited_zar)}</td>
                        {/* Validated is rendered muted beside declared, never instead of
                            it, and NULL shows as words rather than a dash that reads as
                            zero. A month with no statement loaded is a gap in Fleetly's
                            filing and must never look like a finding about the man. */}
                        <td className="px-3 py-1.5 text-right tabular-nums text-muted-foreground">
                          {p?.proven_deposited_zar === null || p?.proven_deposited_zar === undefined ? (
                            <span title="No bank statement covering this month has been loaded.">
                              no statement
                            </span>
                          ) : (
                            zar(p.proven_deposited_zar)
                          )}
                        </td>
                        <td className="px-3 py-1.5 text-right tabular-nums">{zar(r.petrol_cash_zar)}</td>
                        <td className="px-3 py-1.5 text-right tabular-nums text-muted-foreground">
                          {p?.proven_petrol_zar === null || p?.proven_petrol_zar === undefined ? (
                            <span
                              title={
                                p && p.fuel_report_covered === false
                                  ? 'No petrol at all is on file for this month.'
                                  : String(p?.position_note ?? '')
                              }
                            >
                              {p && p.fuel_report_covered === false ? 'no petrol on file' : 'not validated'}
                            </span>
                          ) : (
                            zar(p.proven_petrol_zar)
                          )}
                        </td>
                        <td className="px-3 py-1.5 text-right tabular-nums">
                          {Number(r.exact_shifts) === 0 ? (
                            <span className="text-muted-foreground">no complete shift</span>
                          ) : (
                            zar(r.variance_exact_zar, { signed: true })
                          )}
                        </td>
                        <td className="px-3 py-1.5 text-right tabular-nums">{num(r.deposit_slips)}</td>
                        <td className="px-3 py-1.5">
                          {Number(r.part_a_absent) > 0 || Number(r.part_b_absent) > 0 ? (
                            <span className="text-muted-foreground">
                              {Number(r.part_a_absent) > 0 ? `${r.part_a_absent} without Part A` : ''}
                              {Number(r.part_a_absent) > 0 && Number(r.part_b_absent) > 0 ? ', ' : ''}
                              {Number(r.part_b_absent) > 0 ? `${r.part_b_absent} without Part B` : ''}
                            </span>
                          ) : (
                            <span className="text-muted-foreground">none</span>
                          )}
                        </td>
                      </tr>
                      {isOpen && (
                        <tr className="border-t border-border bg-muted/20">
                          <td colSpan={11} className="p-3">
                            <DriverOutstandingPanel
                              driverId={id}
                              driverName={String(r.driver_name)}
                              mayClose={canEditDrivers || canEditFleet}
                              onChanged={() => position.trigger({ month }, { skipCache: true })}
                            />
                            {p?.deposit_proof_note && (
                              <p className="mb-2 text-xs text-muted-foreground">{p.deposit_proof_note}</p>
                            )}
                            <DriverCreditPanel
                              driverId={id}
                              driverName={String(r.driver_name)}
                              credits={(candidates.data as Row[] | undefined) ?? []}
                              loading={candidates.loading}
                              mayAttribute={canEditDrivers || canEditFleet}
                              busy={attribute.loading || unattribute.loading}
                              onAttribute={async (args) => {
                                const res = (await attribute.trigger(
                                  { ...args, driver_id: id },
                                  { skipCache: true },
                                ).result) as Row
                                if (res?.ok) {
                                  toast.success('Recorded as his.', { description: String(res.note ?? '') })
                                  candidates.trigger({ driver_id: id }, { skipCache: true })
                                  position.trigger({ month }, { skipCache: true })
                                } else {
                                  toast.error('Not recorded', { description: String(res?.error ?? '') })
                                }
                              }}
                              onWithdraw={async (args) => {
                                const res = (await unattribute.trigger(args, { skipCache: true })
                                  .result) as Row
                                if (res?.ok) {
                                  toast.success('Withdrawn.')
                                  candidates.trigger({ driver_id: id }, { skipCache: true })
                                  position.trigger({ month }, { skipCache: true })
                                } else {
                                  toast.error('Not withdrawn', { description: String(res?.error ?? '') })
                                }
                              }}
                            />
                            <div className="mt-3">
                              <Button
                                type="button"
                                size="sm"
                                variant="outline"
                                onClick={() => {
                                  setUploadFor(id)
                                  setIntakeRows([])
                                  setUploadingDoc(true)
                                }}
                              >
                                <Upload className="mr-1.5 h-3.5 w-3.5" />
                                Add {String(r.driver_name).split(' ')[0]}&rsquo;s slips and receipts
                              </Button>
                            </div>
                          </td>
                        </tr>
                      )}
                      </Fragment>
                      )
                    })}
                  </tbody>
                </table>
              </div>
            )}
            <p className="mt-2 flex items-start gap-2 text-xs text-muted-foreground">
              <Users className="mt-0.5 h-3.5 w-3.5 shrink-0" />
              Ranked by the exact variance only. A man whose Part B never arrived would otherwise sit
              at the top of a list headed &ldquo;unexplained cash&rdquo; because of a webhook, and the
              first name on that list is the first name in somebody&rsquo;s mouth.
            </p>
          </TabsContent>

          <TabsContent value="position" className="p-3">
            <p className="mb-3 text-xs text-muted-foreground">
              Two figures for every man, and they are never added together. What he owes on what he
              DECLARED, and what he owes on what Fleetly can PROVE. The difference between them is
              evidence we do not hold, and it is ours to go and find before any of it is put to him.
            </p>
            {position.loading ? (
              <div className="flex items-center gap-2 p-6 text-sm text-muted-foreground">
                <Spinner className="h-4 w-4" /> Loading…
              </div>
            ) : (
              <ul className="space-y-2">
                {((position.data as Row[] | undefined) ?? []).map((r) => {
                  const isOpen = openDriver === String(r.driver_id)
                  return (
                    <li key={String(r.driver_id)} className="rounded-md border border-border">
                      <button
                        type="button"
                        className="flex w-full flex-wrap items-baseline gap-2 p-3 text-left hover:bg-accent/50"
                        onClick={() => setOpenDriver(isOpen ? null : String(r.driver_id))}
                      >
                        <span className="text-sm font-medium text-foreground">{r.driver_name}</span>
                        <span className="text-xs text-muted-foreground">
                          {num(r.shifts)} shifts · {num(r.exact_shifts)} judgeable
                        </span>
                        <span className="ml-auto text-xs text-muted-foreground">declared</span>
                        <span className="tabular-nums text-sm font-semibold">
                          {zar(r.owed_declared_zar, { signed: true })}
                        </span>
                        <span className="text-xs text-muted-foreground">proven</span>
                        <span className="tabular-nums text-sm text-muted-foreground">
                          {r.owed_proven_zar === null || r.owed_proven_zar === undefined
                            ? 'cannot say yet'
                            : zar(r.owed_proven_zar, { signed: true })}
                        </span>
                      </button>
                      {isOpen && (
                        <div className="p-3 pt-0">
                          <DriverCashPanel
                            position={r}
                            mayResolve={canEditFleet}
                            busy={resolve.loading}
                            resolutions={((resolutions.data as Row[] | undefined) ?? []).filter(
                              (x) => String(x.driver_id) === String(r.driver_id),
                            )}
                            onResolve={async (args) => {
                              const res = (await resolve.trigger(args, { skipCache: true })
                                .result) as Row
                              if (res?.ok) {
                                toast.success('Recorded against the driver and the month.')
                                resolutions.trigger({ period_month: month }, { skipCache: true })
                                position.trigger({ month }, { skipCache: true })
                              } else {
                                toast.error('Not recorded', {
                                  description: String(res?.error ?? ''),
                                })
                              }
                            }}
                          />
                        </div>
                      )}
                    </li>
                  )
                })}
                {!position.loading && ((position.data as Row[] | undefined) ?? []).length === 0 && (
                  <li className="p-6 text-center text-sm text-muted-foreground">
                    No shift is recorded for this month yet.
                  </li>
                )}
              </ul>
            )}
          </TabsContent>

          <TabsContent value="shifts" className="p-3">
            <ul className="space-y-2">
              {((shifts.data as Row[] | undefined) ?? []).map((r) => (
                <li key={String(r.shift_id)} className="rounded-md border border-border p-3">
                  <div className="flex flex-wrap items-baseline gap-2">
                    <span className="text-sm font-medium text-foreground">{r.driver_name}</span>
                    <span className="text-xs text-muted-foreground">
                      {r.shift_date} · {r.shift_window === 'am_pm' ? 'day' : 'night'} ·{' '}
                      {r.vehicle_registration ?? 'no vehicle'}
                    </span>
                    <Chip tone={cashStatusTone(String(r.cash_status))} className="ml-auto">
                      {cashStatusLabel(String(r.cash_status))}
                    </Chip>
                  </div>
                  <div className="mt-2 grid gap-x-4 gap-y-1 text-xs sm:grid-cols-3 lg:grid-cols-6">
                    <div>
                      <span className="text-muted-foreground">Uber cash</span>
                      <div className="tabular-nums">{zar(r.uber_cash_collected_zar)}</div>
                    </div>
                    <div>
                      <span className="text-muted-foreground">Private trips</span>
                      <div className="tabular-nums">{zar(r.private_trip_cash_zar)}</div>
                    </div>
                    <div>
                      <span className="text-muted-foreground">Banked</span>
                      <div className="tabular-nums">{zar(r.deposited_zar)}</div>
                    </div>
                    <div>
                      <span className="text-muted-foreground">Petrol cash</span>
                      <div className="tabular-nums">{zar(r.petrol_cash_zar)}</div>
                    </div>
                    <div>
                      <span className="text-muted-foreground">Team mate</span>
                      <div className="tabular-nums">
                        {zar(Number(r.partner_cash_out_zar ?? 0) - Number(r.partner_cash_in_zar ?? 0), {
                          signed: true,
                        })}
                      </div>
                    </div>
                    <div>
                      <span className="text-muted-foreground">
                        Variance ({String(r.variance_bound)})
                      </span>
                      <div className="tabular-nums">{zar(r.variance_zar, { signed: true })}</div>
                    </div>
                  </div>
                  <p className="mt-2 text-xs text-muted-foreground">{r.cash_note}</p>
                  <p className="mt-1 text-[11px] text-muted-foreground">
                    Uber figure from {r.uber_cash_source ?? 'no source'} ·{' '}
                    {Number(r.deposit_slip_count) > 0
                      ? `${r.deposit_slip_count} deposit slip on file, not read`
                      : 'no deposit slip on file'}
                  </p>
                </li>
              ))}
              {!shifts.loading && ((shifts.data as Row[] | undefined) ?? []).length === 0 && (
                <li className="p-6 text-center text-sm text-muted-foreground">
                  Nothing on this month needs a person.
                </li>
              )}
            </ul>
          </TabsContent>

          <TabsContent value="deposits" className="p-3">
            <div className="overflow-x-auto rounded-md border border-border">
              <table className="w-full text-xs">
                <thead className="bg-muted/50">
                  <tr>
                    <th className="px-3 py-2 text-left font-medium">Driver</th>
                    <th className="px-3 py-2 text-left font-medium">Shift date</th>
                    <th className="px-3 py-2 text-right font-medium">Declared</th>
                    <th className="px-3 py-2 text-right font-medium">Matched</th>
                    <th className="px-3 py-2 text-left font-medium">On the Fleetly account</th>
                    <th className="px-3 py-2 text-left font-medium">What it means</th>
                  </tr>
                </thead>
                <tbody>
                  {((deposits.data as Row[] | undefined) ?? []).map((r) => (
                    <tr key={String(r.cash_transaction_id)} className="border-t border-border align-top">
                      <td className="whitespace-nowrap px-3 py-1.5 font-medium">{r.driver_name}</td>
                      <td className="whitespace-nowrap px-3 py-1.5">{r.shift_date}</td>
                      <td className="px-3 py-1.5 text-right tabular-nums">{zar(r.declared_zar)}</td>
                      <td className="px-3 py-1.5 text-right tabular-nums">{zar(r.matched_zar)}</td>
                      <td className="px-3 py-1.5">
                        <Chip tone={bankStatusTone(String(r.bank_status))}>
                          {bankStatusLabel(String(r.bank_status))}
                        </Chip>
                      </td>
                      <td className="px-3 py-1.5 text-muted-foreground">{r.bank_note}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {canViewFinance && (
              <div className="mt-3 flex flex-wrap items-center gap-2">
                {/*
                  The PDF the bank actually sends goes through the Bank Statements
                  page, which reads it and checks the reading against the
                  document's own opening balance, closing balance and printed
                  turnover before loading a line. This button stays for the CSV
                  export, which carries no totals to check against and therefore
                  still needs a person to map its columns and look at the result.
                */}
                <Button variant="brand" asChild>
                  <Link to="/bank-statements">
                    <Landmark className="mr-2 h-4 w-4" /> Bank Statements
                  </Link>
                </Button>
                <Button variant="outline" onClick={() => setImporting('bank')}>
                  <Upload className="mr-2 h-4 w-4" /> Load a CSV export
                </Button>
                <Button
                  variant="outline"
                  onClick={async () => {
                    const to = new Date(
                      Date.UTC(Number(month.slice(0, 4)), Number(month.slice(5, 7)), 0),
                    )
                      .toISOString()
                      .slice(0, 10)
                    await proposals.trigger({ from: month, to }, { skipCache: true })
                  }}
                >
                  <Wand2 className="mr-2 h-4 w-4" /> Find likely matches
                </Button>
                <Button
                  variant="outline"
                  onClick={async () => {
                    const from = month
                    const to = new Date(
                      Date.UTC(Number(month.slice(0, 4)), Number(month.slice(5, 7)), 0),
                    )
                      .toISOString()
                      .slice(0, 10)
                    await matchDeposits.trigger({ from, to, commit: false }, { skipCache: true })
                  }}
                >
                  Preview matching for this month
                </Button>
                {matchDeposits.data != null && (
                  <span className="text-xs text-muted-foreground">
                    {JSON.stringify(matchDeposits.data)}
                  </span>
                )}
                {matchDeposits.data != null &&
                  (matchDeposits.data as Row).dry_run === true &&
                  Number((matchDeposits.data as Row).would_match_same_day ?? 0) +
                    Number((matchDeposits.data as Row).would_match_in_window ?? 0) >
                    0 && (
                    <Button
                      variant="brand"
                      onClick={async () => {
                        const from = month
                        const to = new Date(
                          Date.UTC(Number(month.slice(0, 4)), Number(month.slice(5, 7)), 0),
                        )
                          .toISOString()
                          .slice(0, 10)
                        await matchDeposits.trigger({ from, to, commit: true }, { skipCache: true })
                        deposits.trigger({ month }, { skipCache: true })
                      }}
                    >
                      Record these matches
                    </Button>
                  )}
              </div>
            )}
            {canViewFinance && ((credits.data as Row[] | undefined) ?? []).length > 0 && (
              <>
                <h3 className="mt-5 flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                  <Landmark className="h-3.5 w-3.5" /> Money that arrived and nobody claimed
                </h3>
                <p className="mt-1 text-xs text-muted-foreground">
                  Read this beside the unmatched declarations. A driver recorded as short is very
                  often the same man whose credit is sitting here under a reference nobody
                  recognised.
                </p>
                <div className="mt-2 overflow-x-auto rounded-md border border-border">
                  <table className="w-full text-xs">
                    <thead className="bg-muted/50">
                      <tr>
                        <th className="px-3 py-2 text-left font-medium">Date</th>
                        <th className="px-3 py-2 text-left font-medium">Description</th>
                        <th className="px-3 py-2 text-left font-medium">Reference</th>
                        <th className="px-3 py-2 text-right font-medium">Unallocated</th>
                      </tr>
                    </thead>
                    <tbody>
                      {((credits.data as Row[] | undefined) ?? []).map((r) => (
                        <tr key={String(r.bank_statement_line_id)} className="border-t border-border">
                          <td className="whitespace-nowrap px-3 py-1.5">{r.txn_date}</td>
                          <td className="px-3 py-1.5">{r.description}</td>
                          <td className="px-3 py-1.5">{r.reference}</td>
                          <td className="px-3 py-1.5 text-right tabular-nums">{zar(r.unallocated_zar)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </>
            )}
            {canViewFinance && ((proposals.data as Row[] | undefined) ?? []).length > 0 && (
              <>
                <h3 className="mt-5 flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                  <Wand2 className="h-3.5 w-3.5" /> Likely pairings, for a person to decide
                </h3>
                <p className="mt-1 text-xs text-muted-foreground">
                  Nothing here has been written. An ATM deposit line carries the name the depositor
                  typed at the machine, which is a better key than the amount and is still a person
                  in a hurry: on 11 August 2026 this account took deposits under &ldquo;Steven&rdquo;,
                  &ldquo;Steven Booi&rdquo; and &ldquo;Ntokozo&rdquo; within minutes. Allocate from the
                  row above once you can see why.
                </p>
                <div className="mt-2 overflow-x-auto rounded-md border border-border">
                  <table className="w-full text-xs">
                    <thead className="bg-muted/50">
                      <tr>
                        <th className="px-3 py-2 text-left font-medium">Driver</th>
                        <th className="px-3 py-2 text-left font-medium">Shift</th>
                        <th className="px-3 py-2 text-right font-medium">Unmatched</th>
                        <th className="px-3 py-2 text-left font-medium">Bank line</th>
                        <th className="px-3 py-2 text-right font-medium">Unallocated</th>
                        <th className="px-3 py-2 text-left font-medium">Why</th>
                      </tr>
                    </thead>
                    <tbody>
                      {((proposals.data as Row[] | undefined) ?? []).map((pr) => (
                        <tr
                          key={`${pr.cash_transaction_id}-${pr.bank_statement_line_id}`}
                          className="border-t border-border align-top"
                        >
                          <td className="whitespace-nowrap px-3 py-1.5 font-medium">
                            {pr.driver_name}
                          </td>
                          <td className="whitespace-nowrap px-3 py-1.5">{pr.shift_date}</td>
                          <td className="px-3 py-1.5 text-right tabular-nums">
                            {zar(pr.unmatched_zar)}
                          </td>
                          <td className="px-3 py-1.5">
                            <div>{pr.line_description}</div>
                            <div className="text-muted-foreground">
                              {pr.txn_date} · {pr.line_reference ?? 'no reference'}
                            </div>
                          </td>
                          <td className="px-3 py-1.5 text-right tabular-nums">
                            {zar(pr.unallocated_zar)}
                          </td>
                          <td className="px-3 py-1.5">
                            <Chip
                              tone={
                                pr.confidence === 'high'
                                  ? 'green'
                                  : pr.confidence === 'medium'
                                    ? 'amber'
                                    : 'neutral'
                              }
                            >
                              {String(pr.basis).replace(/_/g, ' ')}
                            </Chip>
                            <div className="mt-1 max-w-md text-muted-foreground">{pr.note}</div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </>
            )}
          </TabsContent>

          <TabsContent value="fuel" className="p-3">
            <div className="mb-3">
              <ReceiptReadPanel
                mayRun={(statementAccess.data as Row | null)?.may_manage === true}
                reading={readBusy}
                applying={applyReceipts.loading}
                progress={readProgress}
                lastRead={lastRead}
                lastApply={lastApply}
                onRead={async (batches) => {
                  // SERIAL, one batch after another, for the reason the batch
                  // size exists: each receipt is fetched and read, and the
                  // dispatcher allows 55 seconds per call.
                  setReadBusy(true)
                  setLastRead(null)
                  let read = 0
                  let cash = 0
                  let failed = 0
                  let last: Row | null = null
                  for (let i = 0; i < batches; i++) {
                    setReadProgress(`Batch ${i + 1} of ${batches}. ${read} read so far.`)
                    const res = (await readDocs.trigger(
                      { kind: 'fuel_receipt', limit: 6 },
                      { skipCache: true },
                    ).result) as Row
                    last = res
                    if (res?.ok === false) break
                    read += Number(res?.read ?? 0)
                    cash += Number(res?.said_cash ?? 0)
                    failed += Number(res?.failed ?? 0)
                    if (Number(res?.read ?? 0) === 0) break
                  }
                  setLastRead(
                    last?.ok === false
                      ? last
                      : { ...(last ?? {}), read, said_cash: cash, failed },
                  )
                  setReadProgress(null)
                  setReadBusy(false)
                }}
                onApply={async (dryRun, toleranceZar) => {
                  const res = (await applyReceipts.trigger(
                    {
                      from: '2025-01-01',
                      to: '2026-12-31',
                      tolerance_zar: toleranceZar,
                      dry_run: dryRun,
                    },
                    { skipCache: true },
                  ).result) as Row
                  setLastApply(res)
                  if (res?.ok === false) {
                    toast.error('Not applied', { description: String(res?.error ?? '') })
                  } else if (!dryRun) {
                    toast.success('Applied.', { description: String(res?.note ?? '') })
                    position.trigger({ month }, { skipCache: true })
                    byDriver.trigger({ month }, { skipCache: true })
                  }
                }}
              />
            </div>
            {((overDeclared.data as Row[] | undefined) ?? []).length > 0 && (
              <div className="mb-3">
                <OverDeclaredPanel
                  rows={(overDeclared.data as Row[] | undefined) ?? []}
                  loading={overDeclared.loading}
                />
              </div>
            )}
            <div className="flex flex-wrap items-center gap-2">
              {canEditFleet && (
                <Button variant="outline" onClick={() => setImporting('fuel')}>
                  <Upload className="mr-2 h-4 w-4" /> Load a fuel report
                </Button>
              )}
              <span className="text-xs text-muted-foreground">
                FuelEasy, an FNB eWallet statement or a card export all load through the same path.
              </span>
            </div>

            <h3 className="mt-4 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
              Cash claimed for petrol against the reports
            </h3>
            <ul className="mt-2 space-y-2">
              {((fuel.data as Row[] | undefined) ?? []).slice(0, 200).map((r) => (
                <li key={String(r.shift_id)} className="rounded-md border border-border p-3">
                  <div className="flex flex-wrap items-baseline gap-2">
                    <span className="text-sm font-medium">{r.driver_name}</span>
                    <span className="text-xs text-muted-foreground">
                      {r.shift_date} · {r.vehicle_registration ?? 'no vehicle'}
                    </span>
                    <Chip tone={fuelStatusTone(String(r.fuel_status))} className="ml-auto">
                      {fuelStatusLabel(String(r.fuel_status))}
                    </Chip>
                  </div>
                  <div className="mt-2 grid gap-x-4 gap-y-1 text-xs sm:grid-cols-3 lg:grid-cols-5">
                    <div>
                      <span className="text-muted-foreground">Claimed in cash</span>
                      <div className="tabular-nums">{zar(r.cash_petrol_claimed_zar)}</div>
                    </div>
                    <div>
                      <span className="text-muted-foreground">Cash on the reports</span>
                      <div className="tabular-nums">{zar(r.fuel_txn_cash_zar)}</div>
                    </div>
                    <div>
                      <span className="text-muted-foreground">Litres bought</span>
                      <div className="tabular-nums">{num(r.fuel_txn_litres, 2)}</div>
                    </div>
                    <div>
                      <span className="text-muted-foreground">Litres charged to him</span>
                      <div className="tabular-nums">{num(r.litres_charged_to_driver, 2)}</div>
                    </div>
                    <div>
                      <span className="text-muted-foreground">Receipts on file</span>
                      <div className="tabular-nums">
                        {num(r.fuel_receipt_photos)} ({num(r.fuel_receipts_read)} read)
                      </div>
                    </div>
                  </div>
                  <p className="mt-2 text-xs text-muted-foreground">{r.fuel_note}</p>
                </li>
              ))}
            </ul>
            <p className="mt-2 text-xs text-muted-foreground">
              Litres bought against litres charged is noisy over one shift, because a tank filled near
              handover belongs to the next shift. Read that column over a run of shifts, never one.
            </p>

            <h3 className="mt-5 flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
              <Fuel className="h-3.5 w-3.5" /> Was the car at the pump
            </h3>
            <div className="mt-2 overflow-x-auto rounded-md border border-border">
              <table className="w-full text-xs">
                <thead className="bg-muted/50">
                  <tr>
                    <th className="px-3 py-2 text-left font-medium">When</th>
                    <th className="px-3 py-2 text-left font-medium">Station</th>
                    <th className="px-3 py-2 text-right font-medium">Amount</th>
                    <th className="px-3 py-2 text-left font-medium">Paid</th>
                    <th className="px-3 py-2 text-left font-medium">Cartrack</th>
                    <th className="px-3 py-2 text-left font-medium">What it means</th>
                  </tr>
                </thead>
                <tbody>
                  {((fuelTx.data as Row[] | undefined) ?? []).map((r) => (
                    <tr key={String(r.fuel_transaction_id)} className="border-t border-border align-top">
                      <td className="whitespace-nowrap px-3 py-1.5">
                        {new Date(String(r.transaction_at)).toLocaleString('en-ZA')}
                      </td>
                      <td className="px-3 py-1.5">{r.station ?? '—'}</td>
                      <td className="px-3 py-1.5 text-right tabular-nums">{zar(r.amount_zar)}</td>
                      <td className="px-3 py-1.5">{r.payment_method}</td>
                      <td className="px-3 py-1.5">
                        <Chip tone={presenceTone(String(r.presence_status))}>
                          {presenceLabel(String(r.presence_status))}
                        </Chip>
                      </td>
                      <td className="px-3 py-1.5 text-muted-foreground">{r.presence_note}</td>
                    </tr>
                  ))}
                  {((fuelTx.data as Row[] | undefined) ?? []).length === 0 && (
                    <tr>
                      <td colSpan={6} className="px-3 py-6 text-center text-muted-foreground">
                        No fuel transaction has been loaded yet.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </TabsContent>

          <TabsContent value="documents" className="p-3">
            <div className="flex flex-wrap items-center gap-2">
              <Button variant="brand" onClick={() => { setIntakeRows([]); setUploadingDoc(true) }}>
                <Upload className="mr-2 h-4 w-4" /> Add documents
              </Button>
              <span className="text-xs text-muted-foreground">
                Drop the whole pile at once: deposit slips, petrol receipts and bank statements
                together, for one driver or for the company. Each one is read and says what it is.
                Anybody may file them; saying what one shows needs Edit drivers or Edit fleet,
                because that is a verdict about a man&rsquo;s money.
              </span>
            </div>

            {canEditFleet && openDocs.length > 0 && (
              <label className="mt-3 flex items-center gap-2 text-xs text-muted-foreground">
                <Checkbox
                  checked={picked.size > 0 && picked.size === openDocs.length}
                  onCheckedChange={(c) =>
                    setPicked(c === true ? new Set(openDocs.map((d) => String(d.document_id))) : new Set())
                  }
                  aria-label="Select every document waiting"
                />
                Select all {num(openDocs.length)} waiting on a person
              </label>
            )}

            <ul className="mt-3 space-y-2">
              {((documents.data as Row[] | undefined) ?? []).map((d) => (
                <DocumentRow
                  key={String(d.document_id)}
                  doc={d}
                  selectable={canEditFleet}
                  selected={picked.has(String(d.document_id))}
                  busy={confirmDoc.loading || rejectDoc.loading || bulkBusy}
                  drivers={driverOptions}
                  onSelect={(checked) =>
                    setPicked((current) => {
                      const next = new Set(current)
                      if (checked) next.add(String(d.document_id))
                      else next.delete(String(d.document_id))
                      return next
                    })
                  }
                  onOpen={async (id) => {
                    const res = (await docUrl.trigger({ document_id: id }, { skipCache: true })
                      .result) as Row
                    if (res?.url) window.open(String(res.url), '_blank', 'noopener')
                    else toast.error('That document could not be opened.')
                  }}
                  onConfirm={async (args) => {
                    const res = (await confirmDoc.trigger(args, { skipCache: true }).result) as Row
                    if (res?.ok) {
                      toast.success('Confirmed.')
                      loadAll()
                    } else {
                      toast.error('Not confirmed', { description: String(res?.error ?? '') })
                    }
                  }}
                  onReject={async (args) => {
                    const res = (await rejectDoc.trigger(args, { skipCache: true }).result) as Row
                    if (res?.ok) {
                      toast.success('Rejected, with the reason on the record.')
                      loadAll()
                    } else {
                      toast.error('Not rejected', { description: String(res?.error ?? '') })
                    }
                  }}
                />
              ))}
              {!documents.loading && ((documents.data as Row[] | undefined) ?? []).length === 0 && (
                <li className="p-6 text-center text-sm text-muted-foreground">
                  No slip or receipt has been uploaded by a manager yet. The 81 slips and 1,117
                  receipts drivers submitted through the form live on the shift record and are not
                  shown here.
                </li>
              )}
            </ul>

            {canEditFleet && (
              <BulkBar
                selected={pickedDocs}
                drivers={driverOptions}
                busy={bulkBusy}
                progress={bulkProgress}
                onClear={() => setPicked(new Set())}
                onAssign={(driverId) => void runBulk('assign', driverId)}
                onConfirm={() => void runBulk('confirm')}
                onReject={(reason) => void runBulk('reject', reason)}
              />
            )}
          </TabsContent>

          <TabsContent value="sources" className="p-3">
            <h3 className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
              <Landmark className="h-3.5 w-3.5" /> Fleetly bank accounts
            </h3>
            {!canViewFinance ? (
              <p className="mt-2 text-xs text-muted-foreground">
                Fleetly&rsquo;s own accounts and statements are finance access. Whether a particular
                driver&rsquo;s deposit cleared is on the Deposits tab and is open to you.
              </p>
            ) : (
              <>
                <div className="mt-2 overflow-x-auto rounded-md border border-border">
                  <table className="w-full text-xs">
                    <thead className="bg-muted/50">
                      <tr>
                        <th className="px-3 py-2 text-left font-medium">Account</th>
                        <th className="px-3 py-2 text-left font-medium">Purpose</th>
                        <th className="px-3 py-2 text-left font-medium">Deposit destination</th>
                        <th className="px-3 py-2 text-left font-medium">Statement covers</th>
                        <th className="px-3 py-2 text-right font-medium">Lines</th>
                      </tr>
                    </thead>
                    <tbody>
                      {((accounts.data as Row[] | undefined) ?? []).map((r) => (
                        <tr key={String(r.id)} className="border-t border-border">
                          <td className="px-3 py-1.5">
                            <div className="font-medium">{r.display_name}</div>
                            <div className="text-muted-foreground">{r.account_number}</div>
                          </td>
                          <td className="px-3 py-1.5">{r.account_purpose}</td>
                          <td className="px-3 py-1.5">
                            <Chip tone={r.is_deposit_destination ? 'green' : 'neutral'}>
                              {r.is_deposit_destination ? 'yes' : 'no'}
                            </Chip>
                          </td>
                          <td className="px-3 py-1.5">
                            {r.covered_from ? `${r.covered_from} to ${r.covered_to}` : 'nothing loaded'}
                          </td>
                          <td className="px-3 py-1.5 text-right tabular-nums">{num(r.lines)}</td>
                        </tr>
                      ))}
                      {((accounts.data as Row[] | undefined) ?? []).length === 0 && (
                        <tr>
                          <td colSpan={5} className="px-3 py-6 text-center text-muted-foreground">
                            No Fleetly bank account is registered. Until one is, no deposit can be
                            checked against a statement.
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
                <p className="mt-2 flex items-start gap-2 text-xs text-muted-foreground">
                  <ListChecks className="mt-0.5 h-3.5 w-3.5 shrink-0" />
                  Registering an account is a database change today, deliberately: the account number
                  is what every deposit verdict compares against, and a typo would report honest
                  drivers as paying into an unrecognised account.
                </p>
              </>
            )}

            <h3 className="mt-5 flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
              <MapPin className="h-3.5 w-3.5" /> Fuel stations
            </h3>
            <div className="mt-2 overflow-x-auto rounded-md border border-border">
              <table className="w-full text-xs">
                <thead className="bg-muted/50">
                  <tr>
                    <th className="px-3 py-2 text-left font-medium">Station</th>
                    <th className="px-3 py-2 text-right font-medium">Transactions</th>
                    <th className="px-3 py-2 text-right font-medium">Spend</th>
                    <th className="px-3 py-2 text-left font-medium">On the map</th>
                  </tr>
                </thead>
                <tbody>
                  {((stations.data as Row[] | undefined) ?? []).map((r) => (
                    <tr key={String(r.id)} className="border-t border-border">
                      <td className="px-3 py-1.5">{r.display_name}</td>
                      <td className="px-3 py-1.5 text-right tabular-nums">{num(r.transactions)}</td>
                      <td className="px-3 py-1.5 text-right tabular-nums">{zar(r.spend_zar)}</td>
                      <td className="px-3 py-1.5">
                        <Chip tone={r.latitude ? 'green' : 'neutral'}>
                          {r.latitude ? 'located' : 'not mapped, no distance check'}
                        </Chip>
                      </td>
                    </tr>
                  ))}
                  {((stations.data as Row[] | undefined) ?? []).length === 0 && (
                    <tr>
                      <td colSpan={4} className="px-3 py-6 text-center text-muted-foreground">
                        Stations appear here as soon as a fuel report names them.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </TabsContent>
        </Tabs>
      </div>

      {(summary.error || byDriver.error || shifts.error) && (
        <div className="flex items-start gap-2 border-t border-destructive/30 bg-destructive/10 p-3 text-sm text-destructive">
          <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
          <span>{summary.error || byDriver.error || shifts.error}</span>
        </div>
      )}

      {importing === 'bank' && (
        <ImportDialog
          title="Load a Fleetly bank statement"
          blurb="This is the only source that can prove a driver's deposit arrived. Until a statement covering the date is loaded, every deposit on that date reads 'no statement loaded', which is the correct answer and not a placeholder. Re-loading an overlapping month is safe: duplicate lines are ignored."
          fields={BANK_FIELDS}
          busy={ingestBank.loading}
          result={ingestBank.data}
          error={ingestBank.error}
          onCancel={() => setImporting(null)}
          onSubmit={async (rows, fileName) => {
            const accountKey =
              ((accounts.data as Row[] | undefined) ?? []).find((a) => a.is_deposit_destination)
                ?.account_key ?? ''
            await ingestBank.trigger(
              { account_key: accountKey, source: 'fnb_csv', file_name: fileName, lines: rows },
              { skipCache: true },
            )
            loadAll()
          }}
        />
      )}

      {uploadingDoc && (
        <CashDocumentUpload
          busy={intakeBusy}
          rows={intakeRows}
          drivers={driverOptions}
          mayLoadStatements={(statementAccess.data as Row | null)?.may_manage === true}
          defaultDriverId={uploadFor ?? undefined}
          onCancel={() => {
            setUploadingDoc(false)
            setUploadFor(null)
            setIntakeRows([])
          }}
          onSubmit={async ({ kind, driver_id, files }: { kind: IntakeKind; driver_id: string | null; files: IntakeFile[] }) => {
            // ONE AT A TIME. Each file is base64 through cockpit_edge_dispatch and
            // this project's pool is sixty wide; CLAUDE.md section 7 records one
            // looping statement taking the cockpit down for forty minutes. A file
            // that fails is recorded on its row and the batch carries on.
            setIntakeBusy(true)
            const rows: IntakeRow[] = files.map((f) => ({
              name: f.file.name,
              state: 'waiting',
              message: 'Waiting',
            }))
            setIntakeRows([...rows])

            const mayLoad = (statementAccess.data as Row | null)?.may_manage === true
            let filed = 0
            let statements = 0
            let failed = 0

            for (let i = 0; i < files.length; i++) {
              const { file, contentBase64 } = files[i]
              rows[i] = { ...rows[i], state: 'sending', message: 'Storing and reading' }
              setIntakeRows([...rows])

              try {
                const res = (await uploadDoc.trigger(
                  {
                    kind,
                    driver_id,
                    filename: file.name,
                    contentType: file.type || 'application/octet-stream',
                    contentBase64,
                  },
                  { skipCache: true },
                ).result) as Row

                if (res?.kind === 'bank_statement') {
                  // Read as a statement, so it goes through the statement door or
                  // not at all. Nothing was stored by the call above.
                  if (!mayLoad) {
                    rows[i] = {
                      ...rows[i],
                      state: 'skipped',
                      kind: 'bank_statement',
                      message:
                        'This is a bank statement. Loading one needs the bank statement permission, which this login does not hold, so nothing was stored. Ask Luyanda, Yolisa or Meseum to load it.',
                    }
                  } else {
                    const account =
                      ((accounts.data as Row[] | undefined) ?? []).find(
                        (a) =>
                          String(a.account_number ?? '').replace(/\D/g, '') ===
                          String(res?.statement?.account_number ?? '').replace(/\D/g, ''),
                      ) ??
                      ((accounts.data as Row[] | undefined) ?? []).find((a) => a.is_deposit_destination)

                    const st = (await uploadStatement.trigger(
                      {
                        account_key: String(account?.account_key ?? ''),
                        filename: file.name,
                        contentType: file.type || 'application/pdf',
                        contentBase64,
                      },
                      { skipCache: true },
                    ).result) as Row

                    if (st?.loaded) {
                      statements += 1
                      rows[i] = {
                        ...rows[i],
                        state: 'done',
                        kind: 'bank_statement',
                        message: `Loaded against ${String(account?.display_name ?? 'the account')}. ${String(
                          st?.comparison?.note ?? '',
                        )}`,
                      }
                    } else {
                      failed += 1
                      rows[i] = {
                        ...rows[i],
                        state: 'failed',
                        kind: 'bank_statement',
                        message: String(st?.error ?? 'The statement was not loaded.'),
                      }
                    }
                  }
                } else if (res?.document_id) {
                  filed += 1
                  rows[i] = {
                    ...rows[i],
                    state: 'done',
                    kind: String(res.kind ?? ''),
                    amount_zar: res?.reading?.amount_zar ?? null,
                    doc_date: res?.reading?.doc_date ?? null,
                    message:
                      res?.reading?.amount_zar == null && res?.reading?.doc_date == null
                        ? 'Stored. Nothing could be read off it, so the figures have to be typed on the board.'
                        : 'Stored and read. Confirm what it shows before it counts.',
                  }
                } else {
                  failed += 1
                  rows[i] = {
                    ...rows[i],
                    state: 'failed',
                    message: String(res?.error ?? 'It was not stored.'),
                  }
                }
              } catch (err) {
                failed += 1
                rows[i] = {
                  ...rows[i],
                  state: 'failed',
                  message: err instanceof Error ? err.message : 'It was not stored.',
                }
              }
              setIntakeRows([...rows])
            }

            setIntakeBusy(false)
            if (filed > 0 || statements > 0) {
              toast.success(
                [
                  filed > 0 ? `${filed} document${filed === 1 ? '' : 's'} filed` : '',
                  statements > 0 ? `${statements} statement${statements === 1 ? '' : 's'} loaded and compared` : '',
                  failed > 0 ? `${failed} did not go through` : '',
                ]
                  .filter((x) => x !== '')
                  .join(', ') + '.',
              )
              loadAll()
            } else if (failed > 0) {
              toast.error('Nothing was stored', {
                description: rows.find((r) => r.state === 'failed')?.message ?? '',
              })
            }
          }}
        />
      )}

      {importing === 'fuel' && (
        <ImportDialog
          title="Load a fuel report"
          blurb="FuelEasy, an FNB eWallet statement or a card export. A line whose vehicle cannot be matched is still stored, with the registration exactly as the report spelled it: hidden spend is worse than unattributed spend."
          fields={FUEL_FIELDS}
          busy={ingestFuel.loading}
          result={ingestFuel.data}
          error={ingestFuel.error}
          onCancel={() => setImporting(null)}
          onSubmit={async (rows, fileName) => {
            await ingestFuel.trigger(
              { source: 'fuel_easy', file_name: fileName, lines: rows },
              { skipCache: true },
            )
            loadAll()
          }}
        />
      )}
    </div>
  )
}
